/**
 * API client.
 *
 * Session state lives in an httpOnly cookie, so there is no token in
 * JavaScript for a script injection to steal and nothing to synchronise
 * between tabs. Every call sends credentials; the dev server proxies /api to
 * the API so the browser sees one origin.
 */

import { request } from './api-client';
import { moduleApi } from './api-modules';

export { ApiError, NetworkError } from './api-client';
export type {
  ChartData,
  ZoneChartData,
  ImageAnnotations,
  ImageMeasurement,
  TreatmentView,
  CatalogueItem,
  WhatsAppDraft,
  InvoiceRow,
  MaterialRow,
  LabOrderRow,
  FinanceReport,
  DentistStatement,
  DentistMonthly,
  ProcedureReport,
  PlatformOverview,
  PlatformClinicDetail,
  ActivationCode,
  SupportGrant,
  PrescriptionDetail,
} from './api-modules';

export interface Membership {
  clinicId: string;
  slug: string;
  nameEn: string;
  nameAr: string;
  roles: string[];
  plan: string | null;
  /** The clinic's money and calendar — every form defaults to these, never to a hard-coded USD. */
  currency: string;
  timezone: string;
  /** Open, or why not. A closed clinic is shown with its reason rather than vanishing. */
  access: { state: 'ok' | 'suspended' | 'closed' | 'paused' | 'expired'; until: string | null; endsAt: string | null };
  isOwner: boolean;
  /** What the clinic practises: 'dental', 'dermatology', 'aesthetics'. */
  specialties?: string[];
  /** How dentists dictate notes in this clinic (clinic settings). */
  dictation?: 'device' | 'browser' | 'off';
}

export interface SessionUser {
  id: string;
  email: string;
  fullName: string;
  locale: 'en' | 'ar';
  isPlatformOwner: boolean;
  roles: string[];
  isDentist: boolean;
  /** Their own membership in the active clinic, when they have one. */
  membershipId: string | null;
  permissions: string[];
  entitlement: { plan: string; features: string[] } | null;
}

export interface Session {
  user: SessionUser;
  memberships: Membership[];
  activeClinicId: string | null;
  /** From /me: whether a code is asked for, and whether at every sign-in. */
  security?: { twoStep: boolean; everySignIn: boolean };
}

/** A code is on its way; the second step of sign-in, activation, registration or reset. */
export interface AuthChallenge {
  id: string;
  purpose: 'sign_in' | 'activate' | 'register' | 'reset';
  /** The address, partly hidden. */
  to: string;
  expiresAt: string;
  resendAfter: string;
  trustOffered: boolean;
  /** Development builds only. */
  devCode?: string;
}

export type SignInResult = Session | { challenge: AuthChallenge };

export function isChallenge(r: SignInResult): r is { challenge: AuthChallenge } {
  return 'challenge' in r && !!r.challenge;
}

export interface PatientSummary {
  id: string;
  patientNumber: string;
  givenName: string;
  familyName: string;
  givenNameAlt: string | null;
  familyNameAlt: string | null;
  birthDate: string | null;
  sex: string | null;
  phone: string | null;
  preferredLocale: string | null;
  archived: boolean;
  updatedAt: string;
}

export interface PatientDetail extends PatientSummary {
  email: string | null;
  whatsappPhone: string | null;
  guardianName: string | null;
  guardianRelation: string | null;
  guardianPhone: string | null;
  notes: string | null;
  version: number;
  sourceId?: string | null;
  referredByMembershipId?: string | null;
  sourceDetail?: string | null;
  sourceName?: { en: string; ar: string | null } | null;
  referredByName?: string | null;
}

export interface AppointmentView {
  id: string;
  status: string;
  reason: string | null;
  provisional: boolean;
  /** Accepted on top of another booking while a device was offline; shown in red until moved. */
  overlapNotedAt?: string | null;
  startsAt: string;
  endsAt: string;
  dentistMembershipId: string;
  dentistName: string;
  resourceId: string | null;
  resource: { en: string; ar: string } | null;
  cancelReason: string | null;
  patient: {
    id: string;
    patientNumber: string;
    givenName: string;
    familyName: string;
    givenNameAlt: string | null;
    familyNameAlt: string | null;
  } | null;
}

export interface DashboardData {
  clinic: { nameEn: string; nameAr: string; timezone: string; currency: string };
  /** Completed, chargeable work not yet on an invoice; null without finance rights. */
  unbilledWork: { count: number; totalMinor: number } | null;
  counts: {
    uniquePatients: number;
    patientScope: 'all' | 'assigned' | 'none';
    appointmentsToday: number;
    upcomingAppointments: number;
    activeMedicalAlerts: number;
  };
  today: {
    id: string;
    status: string;
    reason: string | null;
    startsAt: string;
    endsAt: string;
    dentistName: string;
    patient: {
      givenName: string;
      familyName: string;
      givenNameAlt: string | null;
      familyNameAlt: string | null;
    } | null;
  }[];
  recentActivity: { id: string; action: string; summary: string | null; actor: string | null; at: string }[];
  finance: null;
}

const coreApi = {
  health: () => request<{ status: string; database: string }>('/api/health'),

  login: (email: string, password: string, clinicId?: string) =>
    request<SignInResult>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, ...(clinicId ? { clinicId } : {}) }),
    }),

  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  /** Redeem an activation code: set a password and switch on the waiting membership. */
  activate: (body: { email: string; code: string; password: string; fullName?: string; acceptPolicy?: boolean }) =>
    request<SignInResult>('/api/auth/activate', { method: 'POST', body: JSON.stringify(body) }),

  /** Create an account because a clinic invited this email; the mailbox is proved with a code. */
  register: (body: { email: string; password: string; fullName: string }) =>
    request<SignInResult>('/api/auth/register', { method: 'POST', body: JSON.stringify(body) }),

  /** The six digits from the email. */
  verifyCode: (body: { challengeId: string; code: string; trustDevice?: boolean }) =>
    request<Session>('/api/auth/verify', { method: 'POST', body: JSON.stringify(body) }),

  resendCode: (challengeId: string) =>
    request<{ challenge: AuthChallenge }>('/api/auth/verify/resend', { method: 'POST', body: JSON.stringify({ challengeId }) }),

  forgotPassword: (email: string) =>
    request<{ challenge: AuthChallenge }>('/api/auth/password/forgot', { method: 'POST', body: JSON.stringify({ email }) }),

  resetPassword: (body: { challengeId: string; code: string; newPassword: string }) =>
    request<Session>('/api/auth/password/reset', { method: 'POST', body: JSON.stringify(body) }),

  forgetTrustedDevices: () => request<{ forgotten: number }>('/api/auth/trusted-devices/forget', { method: 'POST' }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true; otherSessionsSignedOut: number }>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  me: () => request<Session>('/api/auth/me'),

  switchClinic: (clinicId: string) =>
    request<{ activeClinicId: string; memberships: Membership[] }>('/api/auth/switch-clinic', {
      method: 'POST',
      body: JSON.stringify({ clinicId }),
    }),

  dashboard: () => request<DashboardData>('/api/dashboard'),

  patients: (params: { q?: string; limit?: number; offset?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.q) search.set('q', params.q);
    if (params.limit) search.set('limit', String(params.limit));
    if (params.offset) search.set('offset', String(params.offset));
    const qs = search.toString();
    return request<{ patients: PatientSummary[]; total: number; scope: string }>(
      `/api/patients${qs ? `?${qs}` : ''}`,
    );
  },

  patient: (id: string) =>
    request<{
      patient: PatientDetail;
      medicalHistory: {
        allergyStatus: string;
        reviewedAt: string | null;
        freeText: string | null;
        medications: { name: string; ingredients?: string[] }[];
      } | null;
      allergies: { id: string; substance: string; reaction: string | null; severity: string }[];
      alerts: { id: string; category: string; diagnosis: string; context: string | null; severity: string }[];
      appointments: { id: string; status: string; reason: string | null; startsAt: string; endsAt: string }[];
    }>(`/api/patients/${id}`),

  createPatient: (body: Record<string, unknown>) =>
    request<{ patient: PatientDetail }>('/api/patients', { method: 'POST', body: JSON.stringify(body) }),

  updatePatient: (id: string, body: Record<string, unknown>) =>
    request<{ patient: PatientDetail }>(`/api/patients/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  appointments: (
    from: string,
    to: string,
    filters: { dentistId?: string; resourceId?: string; patientId?: string; includeCancelled?: boolean } = {},
  ) => {
    const q = new URLSearchParams({ from, to });
    if (filters.dentistId) q.set('dentistId', filters.dentistId);
    if (filters.resourceId) q.set('resourceId', filters.resourceId);
    if (filters.patientId) q.set('patientId', filters.patientId);
    if (filters.includeCancelled) q.set('includeCancelled', 'true');
    return request<{ appointments: AppointmentView[] }>(`/api/appointments?${q.toString()}`);
  },

  /** Move an appointment. The same appointment at a new time, not a rebooking. */
  rescheduleAppointment: (id: string, body: Record<string, unknown>) =>
    request<{ id: string; startsAt: string; endsAt: string }>(`/api/appointments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  setAppointmentStatus: (id: string, body: Record<string, unknown>) =>
    request<{ id: string; status: string }>(`/api/appointments/${id}/status`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  appointmentResources: () =>
    request<{ dentists: { membershipId: string; name: string }[]; chairs: { id: string; en: string; ar: string }[] }>(
      '/api/appointments/resources',
    ),

  createAppointment: (body: Record<string, unknown>) =>
    request<{ appointment: { id: string; startsAt: string; endsAt: string } }>('/api/appointments', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

/**
 * The single client the pages import. Core endpoints and module endpoints are
 * merged here so callers never have to know which file a method lives in.
 */
export const api = { ...coreApi, ...moduleApi };
